import { useEffect, useRef } from 'react'

import { useNavigate, useSearchParams } from 'react-router-dom'

import { LOGIN_STORAGE_USER, REDIRECT_TO_STORAGE_KEY } from 'auth/domain/User'

import useOauth2Flow from 'auth/ui/hooks/useOauth2'
import { AnalyticsEvent } from 'core/analytics/events'
import useAnalytics from 'core/analytics/useAnalytics'
import { LocalStorageClient } from 'core/infra/storage/LocalStorageClient'

const LoginThroughBIP = () => {
  const { logIn, isLoggedIn } = useOauth2Flow()
  const navigate = useNavigate()
  const { track } = useAnalytics()
  const hasAuthenticatedRef = useRef(false)
  const startTrackedRef = useRef(false)
  const storage = new LocalStorageClient()

  const [searchParams] = useSearchParams()

  function getUserData() {
    try {
      const userData = storage.get<string | { email: string; [key: string]: unknown }>(LOGIN_STORAGE_USER)
      if (!userData) return null

      let user: { email: string; [key: string]: unknown }
      if (typeof userData === 'string') {
        user = JSON.parse(userData)
      } else {
        user = userData
      }

      return {
        email: user.email,
      }
    } catch {
      return null
    }
  }

  useEffect(() => {
    async function redirect() {
      const redirectTo = searchParams.get('redirect_to')

      const isLogged = await isLoggedIn()
      if (isLogged) {
        hasAuthenticatedRef.current = true
        navigate(redirectTo ?? '/demo')
        return
      }

      if (redirectTo) {
        localStorage.setItem(REDIRECT_TO_STORAGE_KEY, redirectTo)
      }

      // Track redirect to BIP
      // BIP authentication events tracked:
      // - Authentication (authStage: 'start'): When user is redirected to BIP OAuth provider
      // - Authentication (authStage: 'success'): When authentication succeeds (tracked in LoginThroughBIPCallback)
      // - Authentication (authStage: 'failure'): When authentication fails (tracked in LoginThroughBIPCallback)
      if (!startTrackedRef.current) {
        startTrackedRef.current = true
        const userData = getUserData()
        track(AnalyticsEvent.Authentication, { 
          authFlow: 'BIP', 
          authStage: 'RedirectedToBIP',
          ...(userData ? { email: userData.email } : {})
        })
      }
      
      logIn()
    }

    redirect()
  }, [logIn, isLoggedIn, navigate, searchParams, track])

  return null
}

export default LoginThroughBIP
